import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Wallet,
  Plus,
  Loader2,
  FileText,
  AlertTriangle,
  Rocket,
  Trash2,
  Eye,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  BadgeEuro,
  Settings,
  Save,
  TrendingUp,
  Receipt,
  Users,
  Award,
  Download,
  Printer,
  Search,
  Upload,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTH_NAMES = [
  '', 'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const STATUS_COLORS = {
  concluido: 'bg-emerald-100 text-emerald-700',
  pedido_chamada: 'bg-slate-100 text-slate-700',
  validacao_documentos: 'bg-blue-100 text-blue-700',
  chamada_efectuada: 'bg-sky-100 text-sky-700',
  pendente_documentos: 'bg-amber-100 text-amber-700',
  registado_comercializadora: 'bg-teal-100 text-teal-700',
  anulado_comercializadora: 'bg-red-100 text-red-700',
  anulado: 'bg-red-100 text-red-700',
};

const CATEGORIES = {
  super_admin: 'Super Admin',
  gestor: 'Gestor',
  parceiro: 'Parceiro',
  chefe_equipa: 'Chefe Equipa',
  comercial: 'Comercial',
  angariador: 'Angariador',
  admin: 'Admin',
};

const COMPANIES = {
  luz_gas: [
    { value: 'edp', label: 'EDP' },
    { value: 'endesa', label: 'Endesa' },
    { value: 'galp', label: 'Galp' },
    { value: 'iberdrola', label: 'Iberdrola' },
    { value: 'repsol', label: 'Repsol' },
    { value: 'gold', label: 'Gold Energy' },
    { value: 'yes', label: 'YES Energy' },
  ],
  solar: [
    { value: 'edp', label: 'EDP Solar' },
    { value: 'galp', label: 'Galp Solar' },
    { value: 'iberdrola', label: 'Iberdrola Solar' },
  ],
  telecomunicacoes: [
    { value: 'nos', label: 'NOS' },
    { value: 'meo', label: 'MEO' },
    { value: 'vodafone', label: 'Vodafone' },
  ],
  alarmes: [
    { value: 'nos_securitas', label: 'NOS Securitas' },
    { value: 'prosegur', label: 'Prosegur' },
  ],
  carregamento: [
    { value: 'edp', label: 'EDP' },
    { value: 'galp', label: 'Galp' },
    { value: 'iberdrola', label: 'Iberdrola' },
  ],
};

const CATEGORY_LABELS = {
  luz_gas: 'Luz e Gas',
  solar: 'Solar',
  telecomunicacoes: 'Telecomunicacoes',
  alarmes: 'Alarmes',
  carregamento: 'Carregamento EV',
};

const fmt = (val) => {
  if (val === null || val === undefined) return '-';
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(val);
};

export const FinancePage = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'gestor'].includes(user?.role);

  const [activeTab, setActiveTab] = useState('autos');
  const [autos, setAutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createMonth, setCreateMonth] = useState(new Date().getMonth() + 1);
  const [createYear, setCreateYear] = useState(new Date().getFullYear());
  const [createFilterType, setCreateFilterType] = useState('todos');
  const [createFilterValue, setCreateFilterValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ users: [], partners: [], teams: [] });

  // Detail
  const [selectedAuto, setSelectedAuto] = useState(null);
  const [autoDetail, setAutoDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [autoSearch, setAutoSearch] = useState('');

  // Config
  const [configs, setConfigs] = useState([]);
  const [editConfig, setEditConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configPartners, setConfigPartners] = useState([]);
  const [configUsers, setConfigUsers] = useState([]);

  // Receipt upload
  const [receiptUpload, setReceiptUpload] = useState(null); // { autoId, userId, userName, type }
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const handleExportExcel = async (autoId) => {
    try {
      const res = await axios.get(`${API_URL}/finance/autos/${autoId}/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auto_${autoId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
    } catch (err) { toast.error('Erro ao exportar Excel'); }
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchAutos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/finance/autos`);
      setAutos(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchConfigs = useCallback(async () => {
    try {
      const [cfgRes, partRes, usrRes] = await Promise.all([
        axios.get(`${API_URL}/finance/config`),
        axios.get(`${API_URL}/partners`),
        axios.get(`${API_URL}/users`),
      ]);
      setConfigs(cfgRes.data);
      setConfigPartners(partRes.data);
      setConfigUsers(usrRes.data.filter(u => ['comercial', 'chefe_equipa', 'angariador'].includes(u.role)));
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchAutos();
    if (isAdmin) fetchConfigs();
  }, [fetchAutos, fetchConfigs, isAdmin]);
  useRefreshListener(fetchAutos);

  // Fetch filter options when create modal opens
  useEffect(() => {
    if (showCreate && isAdmin) {
      const loadOptions = async () => {
        try {
          const [u, p, t] = await Promise.all([
            axios.get(`${API_URL}/users`),
            axios.get(`${API_URL}/partners`),
            axios.get(`${API_URL}/teams`),
          ]);
          setFilterOptions({
            users: u.data.filter(x => ['comercial', 'chefe_equipa'].includes(x.role)),
            partners: p.data,
            teams: t.data,
          });
        } catch (err) { console.error(err); }
      };
      loadOptions();
    }
  }, [showCreate, isAdmin]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = { month: parseInt(createMonth), year: parseInt(createYear) };
      if (createFilterType !== 'todos' && createFilterValue) {
        payload.filter_type = createFilterType;
        payload.filter_value = createFilterValue;
      }
      await axios.post(`${API_URL}/finance/autos`, payload);
      const filterLabel = createFilterType !== 'todos' ? ` (${createFilterType})` : '';
      toast.success(`Auto de ${MONTH_NAMES[createMonth]} ${createYear}${filterLabel} criado`);
      setShowCreate(false);
      setCreateFilterType('todos');
      setCreateFilterValue('');
      fetchAutos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar auto'); }
    finally { setCreating(false); }
  };

  const handleLaunch = async (autoId) => {
    try {
      const res = await axios.put(`${API_URL}/finance/autos/${autoId}/lancar`);
      toast.success(res.data.message);
      fetchAutos();
      if (selectedAuto === autoId) fetchAutoDetail(autoId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const handleDelete = async (autoId) => {
    try {
      await axios.delete(`${API_URL}/finance/autos/${autoId}`);
      toast.success('Auto eliminado');
      if (selectedAuto === autoId) { setSelectedAuto(null); setAutoDetail(null); }
      fetchAutos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const fetchAutoDetail = async (autoId) => {
    setLoadingDetail(true);
    setSelectedAuto(autoId);
    try {
      const res = await axios.get(`${API_URL}/finance/autos/${autoId}`);
      setAutoDetail(res.data);
      const exp = {};
      (res.data.entries || []).forEach(e => { exp[e.user_id] = true; });
      setExpandedUsers(exp);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setLoadingDetail(false); }
  };

  const toggleUser = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  // Commission config
  const handleSaveConfig = async () => {
    if (!editConfig) return;
    setSavingConfig(true);
    try {
      await axios.post(`${API_URL}/finance/config`, {
        service_category: editConfig.service_category,
        company: editConfig.company,
        scope_type: editConfig.scope_type || 'global',
        scope_id: editConfig.scope_id || '',
        scope_name: editConfig.scope_name || '',
        receita_empresa: parseFloat(editConfig.receita_empresa) || 0,
        comissao_comercial: parseFloat(editConfig.comissao_comercial) || 0,
        comissao_parceiro: parseFloat(editConfig.comissao_parceiro) || 0,
        bonus_chefe_tipo: editConfig.bonus_chefe_tipo || 'percentagem',
        bonus_chefe_valor: parseFloat(editConfig.bonus_chefe_valor) || 10,
        chargeback_meses: parseInt(editConfig.chargeback_meses) || 0,
      });
      toast.success('Configuracao guardada');
      setEditConfig(null);
      fetchConfigs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSavingConfig(false); }
  };

  const handleReceiptUpload = async (e) => {
    if (!receiptUpload) return;
    const file = e.target.files?.[0];
    if (!file) return;
    // Validação client-side: tamanho máximo 10MB
    const MAX_SIZE_MB = 10;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Ficheiro demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo permitido: ${MAX_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }
    const okType = /\.(pdf|jpg|jpeg|png|webp)$/i.test(file.name) || ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'].includes(file.type);
    if (!okType) {
      toast.error('Formato não suportado. Aceita-se apenas PDF, JPG, JPEG, PNG ou WEBP.');
      e.target.value = '';
      return;
    }
    setUploadingReceipt(true);
    try {
      // Upload file first
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API_URL}/files/upload`, formData);
      const fileUrl = uploadRes.data.url;
      
      const endpoint = receiptUpload.type === 'receipt' 
        ? `${API_URL}/finance/autos/${receiptUpload.autoId}/receipt`
        : `${API_URL}/finance/autos/${receiptUpload.autoId}/payment-proof`;
      
      await axios.post(endpoint, {
        file_url: fileUrl,
        file_name: file.name,
        user_id: receiptUpload.userId,
      });
      
      toast.success(receiptUpload.type === 'receipt' ? 'Recibo enviado' : 'Comprovativo enviado — Liquidado');
      setReceiptUpload(null);
      // Refresh detail
      fetchAutoDetail(receiptUpload.autoId);
    } catch (err) {
      toast.error('Erro ao enviar ficheiro');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    try {
      await axios.delete(`${API_URL}/finance/config/${configId}`);
      toast.success('Eliminada');
      fetchConfigs();
    } catch (err) { toast.error('Erro'); }
  };

  if (loading && !autos.length) {
    return <Layout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></Layout>;
  }

  // ===== AUTO DETAIL VIEW =====
  if (selectedAuto && autoDetail) {
    const gt = autoDetail.grand_totals || {};
    const viewerRole = autoDetail.viewer_role;
    const bonusEquipa = autoDetail.bonus_equipa;

    // Filter entries by search
    const filteredEntries = (autoDetail.entries || []).filter(e => {
      if (!autoSearch) return true;
      const q = autoSearch.toLowerCase();
      return e.user_name?.toLowerCase().includes(q) || e.partner_name?.toLowerCase().includes(q);
    });

    return (
      <Layout>
        <div className="space-y-5" data-testid="auto-detail-page">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setSelectedAuto(null); setAutoDetail(null); setAutoSearch(''); }} data-testid="back-to-autos-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#2D3E6F]" />
                  Auto de {MONTH_NAMES[autoDetail.month]} {autoDetail.year}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${autoDetail.status === 'lancado' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {autoDetail.status === 'lancado' ? 'Lancado' : 'Rascunho'}
                  </span>
                  <span className="text-sm text-slate-500">{filteredEntries.length} comerciais</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isAdmin && autoDetail.status === 'rascunho' && (
                <Button onClick={() => handleLaunch(autoDetail.id)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="launch-auto-detail-btn">
                  <Rocket className="w-4 h-4" /> Lancar Auto
                </Button>
              )}
              <Button variant="outline" onClick={() => handleExportExcel(autoDetail.id)} className="gap-2" data-testid="export-excel-btn">
                <Download className="w-4 h-4" /> Excel
              </Button>
              <Button variant="outline" onClick={handlePrint} className="gap-2 print:hidden" data-testid="print-auto-btn">
                <Printer className="w-4 h-4" /> Imprimir
              </Button>
              {isAdmin && (
                <Button variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 gap-2" onClick={() => { if (window.confirm('Apagar este auto?')) handleDelete(autoDetail.id); }} data-testid="delete-auto-detail-btn">
                  <Trash2 className="w-4 h-4" /> Apagar
                </Button>
              )}
            </div>
          </div>

          {/* KPI Cards */}
          {isAdmin ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <KPICard icon={<Receipt className="w-5 h-5 text-blue-400" />} label="Receita Total" value={fmt(gt.receita)} color="from-blue-600 to-blue-800" />
              <KPICard icon={<Users className="w-5 h-5 text-amber-400" />} label="Comissoes Comerciais" value={fmt(gt.comissao_comercial)} color="from-amber-600 to-amber-800" />
              <KPICard icon={<Award className="w-5 h-5 text-purple-400" />} label="Comissoes Parceiros" value={fmt(gt.comissao_parceiro)} color="from-purple-600 to-purple-800" />
              <KPICard icon={<Award className="w-5 h-5 text-orange-400" />} label="Bonus Chefes" value={fmt(gt.bonus_chefe)} color="from-orange-600 to-orange-800" />
              <KPICard icon={<TrendingUp className="w-5 h-5 text-emerald-400" />} label="Faturacao (Lucro)" value={fmt(gt.lucro)} color="from-emerald-600 to-emerald-800" highlight />
            </div>
          ) : viewerRole === 'parceiro' ? (
            <Card className="rounded-2xl border-slate-100 bg-gradient-to-r from-purple-600 to-purple-800">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-200">Total Comissoes Parceiro</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="auto-grand-total">{fmt(gt.comissao_parceiro)}</p>
                </div>
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
                  <BadgeEuro className="w-7 h-7 text-white" />
                </div>
              </CardContent>
            </Card>
          ) : viewerRole === 'chefe_equipa' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="rounded-2xl border-slate-100 bg-gradient-to-r from-[#2D3E6F] to-[#1a2a4a]">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-300">Minha Comissao</p>
                    <p className="text-3xl font-bold text-white mt-1">{fmt(gt.comissao_comercial)}</p>
                  </div>
                  <BadgeEuro className="w-7 h-7 text-white/50" />
                </CardContent>
              </Card>
              {bonusEquipa && (
                <Card className="rounded-2xl border-slate-100 bg-gradient-to-r from-orange-600 to-orange-800">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-200">Bonus Equipa ({bonusEquipa.contracts?.length || 0} contratos)</p>
                      <p className="text-3xl font-bold text-white mt-1">{fmt(bonusEquipa.total)}</p>
                    </div>
                    <Award className="w-7 h-7 text-white/50" />
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card className="rounded-2xl border-slate-100 bg-gradient-to-r from-[#2D3E6F] to-[#1a2a4a]">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300">Minha Comissao Total</p>
                  <p className="text-3xl font-bold text-white mt-1" data-testid="auto-grand-total">{fmt(gt.comissao_comercial)}</p>
                </div>
                <BadgeEuro className="w-7 h-7 text-white/50" />
              </CardContent>
            </Card>
          )}

          {/* Chefe bonus details */}
          {viewerRole === 'chefe_equipa' && bonusEquipa && bonusEquipa.contracts?.length > 0 && (
            <Card className="rounded-2xl border-slate-100">
              <CardHeader className="border-b border-slate-100 pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-orange-500" /> Bonus por Contratos da Equipa
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead className="text-xs font-semibold">#</TableHead>
                      <TableHead className="text-xs font-semibold">Cliente</TableHead>
                      <TableHead className="text-xs font-semibold">Empresa</TableHead>
                      <TableHead className="text-xs font-semibold">Comercial</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Bonus</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bonusEquipa.contracts.map((bc, i) => (
                      <TableRow key={i} className={bc.is_chargeback ? 'bg-red-50/30' : ''}>
                        <TableCell className="font-mono text-xs">{bc.codigo}</TableCell>
                        <TableCell className="text-sm">{bc.nome_cliente}</TableCell>
                        <TableCell className="text-sm">{bc.company_name}</TableCell>
                        <TableCell className="text-sm">{bc.comercial_name}</TableCell>
                        <TableCell className={`text-right font-mono font-bold ${bc.bonus < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {fmt(bc.bonus)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-slate-50 border-t-2 border-slate-200">
                      <TableCell colSpan={4} className="text-right font-semibold text-sm">Total Bonus</TableCell>
                      <TableCell className="text-right font-mono font-bold text-orange-600">{fmt(bonusEquipa.total)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Search filter */}
          {isAdmin && (autoDetail.entries || []).length > 1 && (
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Pesquisar comercial ou parceiro..."
                value={autoSearch}
                onChange={(e) => setAutoSearch(e.target.value)}
                className="pl-10 h-10 rounded-lg"
                data-testid="auto-search-input"
              />
            </div>
          )}

          {/* User entries */}
          <div className="space-y-3">
            {filteredEntries.length === 0 && (
              <Card className="rounded-2xl border-slate-100">
                <CardContent className="p-12 text-center text-slate-400">{autoSearch ? 'Nenhum resultado para esta pesquisa' : 'Sem contratos neste periodo'}</CardContent>
              </Card>
            )}
            {filteredEntries.map((entry) => (
              <Card key={entry.user_id} className="rounded-2xl border-slate-100 overflow-hidden" data-testid={`auto-entry-${entry.user_id}`}>
                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggleUser(entry.user_id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2D3E6F] flex items-center justify-center text-white text-sm font-bold">
                      {entry.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{entry.user_name}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{CATEGORIES[entry.role] || entry.role}</span>
                      </div>
                      <span className="text-xs text-slate-400">{entry.partner_name} - {entry.contract_count} contrato{entry.contract_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {isAdmin ? (
                      <div className="text-right">
                        <div className="text-xs text-slate-400">Comissao / Receita</div>
                        <span className="text-base font-bold text-amber-600">{fmt(entry.totals?.comissao_comercial)}</span>
                        <span className="text-xs text-slate-400 mx-1">/</span>
                        <span className="text-sm font-medium text-blue-600">{fmt(entry.totals?.receita)}</span>
                      </div>
                    ) : viewerRole === 'parceiro' ? (
                      <span className="text-lg font-bold text-purple-600">{fmt(entry.totals?.comissao_parceiro)}</span>
                    ) : (
                      <span className={`text-lg font-bold ${entry.display_total >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(entry.display_total)}</span>
                    )}
                    {expandedUsers[entry.user_id] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>

                {expandedUsers[entry.user_id] && (
                  <div className="border-t border-slate-100">
                    <div className="overflow-x-auto">
                      <Table className="min-w-[900px]">
                        <TableHeader>
                          <TableRow className="bg-slate-50/80">
                            <TableHead className="font-semibold text-xs w-14">#</TableHead>
                            <TableHead className="font-semibold text-xs">Cliente</TableHead>
                            <TableHead className="font-semibold text-xs">NIF</TableHead>
                            <TableHead className="font-semibold text-xs">Tipo Produto</TableHead>
                            <TableHead className="font-semibold text-xs">Empresa</TableHead>
                            <TableHead className="font-semibold text-xs">Estado</TableHead>
                            <TableHead className="font-semibold text-xs">CPE</TableHead>
                            <TableHead className="font-semibold text-xs">Data</TableHead>
                            <TableHead className="font-semibold text-xs">Pagamento</TableHead>
                            {isAdmin && <TableHead className="font-semibold text-xs text-right">Receita</TableHead>}
                            <TableHead className="font-semibold text-xs text-right">
                              {viewerRole === 'parceiro' ? 'Com. Parceiro' : 'Comissao'}
                            </TableHead>
                            {isAdmin && <TableHead className="font-semibold text-xs text-right">Com. Parceiro</TableHead>}
                            {isAdmin && <TableHead className="font-semibold text-xs text-right">Bonus Chefe</TableHead>}
                            {isAdmin && <TableHead className="font-semibold text-xs text-right">Lucro</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entry.contracts.map((c) => (
                            <TableRow key={c.contract_id} className={`hover:bg-slate-50/50 ${c.is_chargeback ? 'bg-red-50/30' : c.is_correction ? 'bg-orange-50/40 border-l-2 border-orange-400' : ''}`} data-testid={`auto-contract-row-${c.contract_id}`}>
                              <TableCell className="font-mono text-xs text-slate-500">
                                {c.is_correction && <span className="text-orange-600 font-bold mr-1" title={`Correção: ${c.correction_from || '?'} → ${c.correction_to || '?'}`}>↻</span>}
                                {c.codigo}
                              </TableCell>
                              <TableCell className="font-medium text-sm">{c.nome_cliente}</TableCell>
                              <TableCell className="font-mono text-xs">{c.nif || '-'}</TableCell>
                              <TableCell className="text-xs">{c.tipo_produto || '-'}</TableCell>
                              <TableCell className="text-sm">{c.company_name}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-600'}`}>{c.status_label}</span>
                              </TableCell>
                              <TableCell className="font-mono text-xs">{c.cpe || '-'}</TableCell>
                              <TableCell className="text-xs text-slate-500">{c.data_contrato || c.created_at?.slice(0, 10) || '-'}</TableCell>
                              <TableCell>
                                {c.is_chargeback ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                    <AlertTriangle className="w-3 h-3" /> Chargeback
                                  </span>
                                ) : (
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    c.estado_pagamento === 'pago' ? 'bg-emerald-100 text-emerald-700' :
                                    c.estado_pagamento === 'a pagamento' ? 'bg-blue-100 text-blue-700' :
                                    c.estado_pagamento === 'anulado' ? 'bg-red-100 text-red-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                    {c.estado_pagamento === 'pago' ? 'Pago' :
                                     c.estado_pagamento === 'a pagamento' ? 'A Pagamento' :
                                     c.estado_pagamento === 'anulado' ? 'Anulado' :
                                     'Pendente'}
                                  </span>
                                )}
                              </TableCell>
                              {isAdmin && <TableCell className="text-right font-mono text-sm">{fmt(c.receita_final)}</TableCell>}
                              <TableCell className={`text-right font-mono text-sm font-bold ${
                                (viewerRole === 'parceiro' ? c.comissao_parceiro_final : c.comissao_comercial_final) < 0 ? 'text-red-600' : 'text-emerald-600'
                              }`}>
                                {viewerRole === 'parceiro' ? fmt(c.comissao_parceiro_final) : fmt(c.comissao_comercial_final)}
                              </TableCell>
                              {isAdmin && <TableCell className="text-right font-mono text-sm">{fmt(c.comissao_parceiro_final)}</TableCell>}
                              {isAdmin && <TableCell className="text-right font-mono text-sm">{fmt(c.bonus_chefe_final)}</TableCell>}
                              {isAdmin && <TableCell className={`text-right font-mono text-sm font-bold ${c.lucro_final < 0 ? 'text-red-600' : c.lucro_final > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {fmt(c.lucro_final)}
                              </TableCell>}
                            </TableRow>
                          ))}
                          <TableRow className="bg-slate-50 border-t-2 border-slate-200">
                            <TableCell colSpan={9} className="text-right font-semibold text-sm text-slate-700">
                              Total ({entry.contract_count} contrato{entry.contract_count !== 1 ? 's' : ''})
                            </TableCell>
                            {isAdmin && <TableCell className="text-right font-mono text-sm font-semibold text-blue-700">{fmt(entry.totals?.receita)}</TableCell>}
                            <TableCell className={`text-right font-mono text-sm font-bold ${
                              (viewerRole === 'parceiro' ? entry.totals?.comissao_parceiro : entry.totals?.comissao_comercial) < 0 ? 'text-red-600' : 'text-emerald-600'
                            }`}>
                              {viewerRole === 'parceiro' ? fmt(entry.totals?.comissao_parceiro) : fmt(entry.totals?.comissao_comercial)}
                            </TableCell>
                            {isAdmin && <TableCell className="text-right font-mono text-sm font-semibold text-purple-700">{fmt(entry.totals?.comissao_parceiro)}</TableCell>}
                            {isAdmin && <TableCell className="text-right font-mono text-sm font-semibold text-orange-700">{fmt(entry.totals?.bonus_chefe)}</TableCell>}
                            {isAdmin && <TableCell className="text-right font-mono text-sm font-bold text-emerald-700">{fmt(entry.totals?.lucro)}</TableCell>}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                    {/* Receipt and Payment Section */}
                    <div className="p-4 border-t border-slate-100 bg-slate-50/30">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          {/* Payment Status Badge */}
                          {autoDetail?.payment_statuses?.[entry.user_id]?.status === 'liquidado' ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700" data-testid={`payment-status-${entry.user_id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> Liquidado
                            </span>
                          ) : autoDetail?.receipt_counts?.[entry.user_id]?.receipt > 0 ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700" data-testid={`payment-status-${entry.user_id}`}>
                              <Clock className="w-3.5 h-3.5" /> Recibo Enviado — Aguarda Pagamento
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500" data-testid={`payment-status-${entry.user_id}`}>
                              <FileText className="w-3.5 h-3.5" /> Pendente — Aguarda Recibo
                            </span>
                          )}
                          {autoDetail?.receipt_counts?.[entry.user_id]?.receipt > 0 && (
                            <span className="text-xs text-slate-400">{autoDetail.receipt_counts[entry.user_id].receipt} recibo(s)</span>
                          )}
                          {autoDetail?.receipt_counts?.[entry.user_id]?.payment_proof > 0 && (
                            <span className="text-xs text-emerald-500">{autoDetail.receipt_counts[entry.user_id].payment_proof} comprovativo(s)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Upload Receipt (visible to the user or admin) */}
                          {(entry.user_id === user?.id || isAdmin) && autoDetail?.payment_statuses?.[entry.user_id]?.status !== 'liquidado' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1.5 text-xs"
                              onClick={(e) => { e.stopPropagation(); setReceiptUpload({ autoId: autoDetail.id, userId: entry.user_id, userName: entry.user_name, type: 'receipt' }); }}
                              data-testid={`upload-receipt-${entry.user_id}`}
                            >
                              <Upload className="w-3.5 h-3.5" /> Enviar Recibo
                            </Button>
                          )}
                          {/* Upload Payment Proof + Mark Liquidado (admin only) */}
                          {isAdmin && autoDetail?.receipt_counts?.[entry.user_id]?.receipt > 0 && autoDetail?.payment_statuses?.[entry.user_id]?.status !== 'liquidado' && (
                            <Button
                              size="sm"
                              className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700"
                              onClick={(e) => { e.stopPropagation(); setReceiptUpload({ autoId: autoDetail.id, userId: entry.user_id, userName: entry.user_name, type: 'payment_proof' }); }}
                              data-testid={`upload-proof-${entry.user_id}`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Pagar e Liquidar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  // ===== MAIN LIST / TABS VIEW =====
  return (
    <Layout>
      <div className="space-y-6" data-testid="finance-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Wallet className="w-6 h-6 text-[#2D3E6F]" /> Gestao Financeira
            </h1>
            <p className="text-slate-500 mt-1">Autos mensais, comissoes e faturacao</p>
          </div>
          {isAdmin && activeTab === 'autos' && (
            <Button onClick={() => setShowCreate(true)} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="create-auto-btn">
              <Plus className="w-4 h-4" /> Novo Auto
            </Button>
          )}
        </div>

        {/* Tabs - only for admin */}
        {isAdmin && (
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'autos' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveTab('autos')}
              data-testid="tab-autos"
            >
              <FileText className="w-4 h-4 inline mr-1.5" /> Autos
            </button>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'config' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveTab('config')}
              data-testid="tab-config"
            >
              <Settings className="w-4 h-4 inline mr-1.5" /> Configuracao Comissoes
            </button>
          </div>
        )}

        {/* TAB: AUTOS */}
        {activeTab === 'autos' && (
          <>
            {autos.length === 0 ? (
              <Card className="rounded-2xl border-slate-100">
                <CardContent className="p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Sem autos criados</p>
                  {isAdmin && <Button onClick={() => setShowCreate(true)} variant="outline" className="mt-4 gap-2"><Plus className="w-4 h-4" /> Criar primeiro auto</Button>}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {autos.map((auto) => (
                  <Card key={auto.id} className="rounded-2xl border-slate-100 hover:shadow-lg transition-shadow cursor-pointer group" data-testid={`auto-card-${auto.id}`} onClick={() => fetchAutoDetail(auto.id)}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#2D3E6F]/10 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-[#2D3E6F]" />
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${auto.status === 'lancado' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {auto.status === 'lancado' ? 'Lancado' : 'Rascunho'}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#2D3E6F] transition-colors">{MONTH_NAMES[auto.month]} {auto.year}</h3>
                      {auto.filter_type && auto.filter_type !== 'todos' && (
                        <p className="text-xs mt-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 inline-block">
                          {auto.filter_type === 'comercial' ? 'Comercial' : auto.filter_type === 'parceiro' ? 'Parceiro' : 'Equipa'}: {auto.filter_label || auto.filter_value}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">Criado por {auto.created_by_name}</p>
                      {auto.launched_at && <p className="text-xs text-emerald-600 mt-1">Lancado em {new Date(auto.launched_at).toLocaleDateString('pt-PT')}</p>}
                      <div className="flex items-center gap-2 mt-4">
                        <Button variant="outline" size="sm" className="gap-1 flex-1" onClick={(e) => { e.stopPropagation(); fetchAutoDetail(auto.id); }} data-testid={`view-auto-${auto.id}`}>
                          <Eye className="w-3.5 h-3.5" /> Ver
                        </Button>
                        {isAdmin && auto.status === 'rascunho' && (
                          <Button variant="outline" size="sm" className="gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={(e) => { e.stopPropagation(); handleLaunch(auto.id); }}>
                            <Rocket className="w-3.5 h-3.5" /> Lancar
                          </Button>
                        )}
                        {isAdmin && (
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); if (window.confirm('Tem a certeza que quer apagar este auto?')) handleDelete(auto.id); }} data-testid={`delete-auto-${auto.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* TAB: COMMISSION CONFIG */}
        {activeTab === 'config' && isAdmin && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Configurar valores de sistema por empresa. Os valores manuais no contrato sobrepoe estes.</p>
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => setEditConfig({ service_category: 'luz_gas', company: '', receita_empresa: '', comissao_comercial: '', comissao_parceiro: '', bonus_chefe_tipo: 'percentagem', bonus_chefe_valor: 10, scope_type: 'global', scope_id: '', scope_name: '', chargeback_meses: '' })}
                data-testid="add-config-btn"
              >
                <Plus className="w-4 h-4" /> Nova Configuracao
              </Button>
            </div>

            {/* Configs table */}
            <Card className="rounded-2xl border-slate-100">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead className="font-semibold text-xs">Categoria</TableHead>
                      <TableHead className="font-semibold text-xs">Empresa</TableHead>
                      <TableHead className="font-semibold text-xs">Aplica-se a</TableHead>
                      <TableHead className="font-semibold text-xs text-right">Receita</TableHead>
                      <TableHead className="font-semibold text-xs text-right">Com. Comercial</TableHead>
                      <TableHead className="font-semibold text-xs text-right">Com. Parceiro</TableHead>
                      <TableHead className="font-semibold text-xs text-right">Bonus Chefe</TableHead>
                      <TableHead className="font-semibold text-xs text-right">Lucro</TableHead>
                      <TableHead className="font-semibold text-xs w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {configs.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Sem configuracoes. Adicione valores por empresa.</TableCell></TableRow>
                    )}
                    {configs.map((cfg) => {
                      const bonusVal = cfg.bonus_chefe_tipo === 'percentagem'
                        ? (cfg.comissao_comercial || 0) * ((cfg.bonus_chefe_valor || 0) / 100)
                        : (cfg.bonus_chefe_valor || 0);
                      const lucro = (cfg.receita_empresa || 0) - (cfg.comissao_comercial || 0) - (cfg.comissao_parceiro || 0) - bonusVal;
                      return (
                        <TableRow key={cfg.id} className="hover:bg-slate-50/50">
                          <TableCell className="text-sm">{CATEGORY_LABELS[cfg.service_category] || cfg.service_category}</TableCell>
                          <TableCell className="text-sm font-medium">{cfg.company}</TableCell>
                          <TableCell className="text-sm">
                            {(!cfg.scope_type || cfg.scope_type === 'global') ? (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">Todos</span>
                            ) : cfg.scope_type === 'partner' ? (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{cfg.scope_name || 'Parceiro'}</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">{cfg.scope_name || 'Comercial'}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm text-blue-700">{fmt(cfg.receita_empresa)}</TableCell>
                          <TableCell className="text-right font-mono text-sm text-amber-700">{fmt(cfg.comissao_comercial)}</TableCell>
                          <TableCell className="text-right font-mono text-sm text-purple-700">{fmt(cfg.comissao_parceiro)}</TableCell>
                          <TableCell className="text-right text-sm">
                            {cfg.bonus_chefe_tipo === 'percentagem' ? `${cfg.bonus_chefe_valor}%` : fmt(cfg.bonus_chefe_valor)}
                            <span className="text-xs text-slate-400 ml-1">({fmt(bonusVal)})</span>
                          </TableCell>
                          <TableCell className={`text-right font-mono text-sm font-bold ${lucro >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(lucro)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="sm" onClick={() => setEditConfig({ ...cfg })} data-testid={`edit-config-${cfg.id}`}>
                                <Settings className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteConfig(cfg.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Edit/Create Config Dialog */}
            <Dialog open={!!editConfig} onOpenChange={(open) => { if (!open) setEditConfig(null); }}>
              <DialogContent className="sm:max-w-lg" data-testid="config-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-[#2D3E6F]" /> Configuracao de Comissoes
                  </DialogTitle>
                </DialogHeader>
                {editConfig && (
                  <div className="space-y-4 py-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Categoria</Label>
                        <Select value={editConfig.service_category} onValueChange={(v) => setEditConfig({ ...editConfig, service_category: v, company: '' })}>
                          <SelectTrigger className="mt-1" data-testid="config-category"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Empresa</Label>
                        <Select value={editConfig.company} onValueChange={(v) => setEditConfig({ ...editConfig, company: v })}>
                          <SelectTrigger className="mt-1" data-testid="config-company"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                          <SelectContent>
                            {(COMPANIES[editConfig.service_category] || []).map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {/* Scope - Aplica-se a */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Aplica-se a</Label>
                        <Select value={editConfig.scope_type || 'global'} onValueChange={(v) => setEditConfig({ ...editConfig, scope_type: v, scope_id: '', scope_name: '' })}>
                          <SelectTrigger className="mt-1" data-testid="config-scope-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="global">Todos (Global)</SelectItem>
                            <SelectItem value="partner">Parceiro</SelectItem>
                            <SelectItem value="comercial">Comercial</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {editConfig.scope_type === 'partner' && (
                        <div>
                          <Label className="text-xs text-slate-500">Parceiro</Label>
                          <Select value={editConfig.scope_id || ''} onValueChange={(v) => {
                            const p = configPartners.find(x => x.id === v);
                            setEditConfig({ ...editConfig, scope_id: v, scope_name: p?.name || '' });
                          }}>
                            <SelectTrigger className="mt-1" data-testid="config-scope-partner"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                            <SelectContent>
                              {configPartners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {editConfig.scope_type === 'comercial' && (
                        <div>
                          <Label className="text-xs text-slate-500">Comercial</Label>
                          <Select value={editConfig.scope_id || ''} onValueChange={(v) => {
                            const u = configUsers.find(x => x.id === v);
                            setEditConfig({ ...editConfig, scope_id: v, scope_name: u?.name || '' });
                          }}>
                            <SelectTrigger className="mt-1" data-testid="config-scope-user"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                            <SelectContent>
                              {configUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Receita Empresa (EUR)</Label>
                        <Input type="number" step="0.01" value={editConfig.receita_empresa} onChange={(e) => setEditConfig({ ...editConfig, receita_empresa: e.target.value })} className="mt-1" data-testid="config-receita" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Com. Comercial (EUR)</Label>
                        <Input type="number" step="0.01" value={editConfig.comissao_comercial} onChange={(e) => setEditConfig({ ...editConfig, comissao_comercial: e.target.value })} className="mt-1" data-testid="config-comercial" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Com. Parceiro (EUR)</Label>
                        <Input type="number" step="0.01" value={editConfig.comissao_parceiro} onChange={(e) => setEditConfig({ ...editConfig, comissao_parceiro: e.target.value })} className="mt-1" data-testid="config-parceiro" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Bonus Chefe - Tipo</Label>
                        <Select value={editConfig.bonus_chefe_tipo} onValueChange={(v) => setEditConfig({ ...editConfig, bonus_chefe_tipo: v })}>
                          <SelectTrigger className="mt-1" data-testid="config-bonus-tipo"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentagem">Percentagem (%)</SelectItem>
                            <SelectItem value="fixo">Valor Fixo (EUR)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">
                          {editConfig.bonus_chefe_tipo === 'percentagem' ? 'Percentagem (%)' : 'Valor Fixo (EUR)'}
                        </Label>
                        <Input type="number" step="0.01" value={editConfig.bonus_chefe_valor} onChange={(e) => setEditConfig({ ...editConfig, bonus_chefe_valor: e.target.value })} className="mt-1" data-testid="config-bonus-valor" />
                      </div>
                    </div>
                    {/* Preview */}
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-slate-500 mb-2">Pre-visualizacao por contrato:</p>
                      <div className="grid grid-cols-5 gap-2 text-xs">
                        <div><span className="text-slate-400">Receita</span><br /><span className="font-bold text-blue-700">{fmt(parseFloat(editConfig.receita_empresa) || 0)}</span></div>
                        <div><span className="text-slate-400">Comercial</span><br /><span className="font-bold text-amber-700">{fmt(parseFloat(editConfig.comissao_comercial) || 0)}</span></div>
                        <div><span className="text-slate-400">Parceiro</span><br /><span className="font-bold text-purple-700">{fmt(parseFloat(editConfig.comissao_parceiro) || 0)}</span></div>
                        <div><span className="text-slate-400">Bonus Chefe</span><br /><span className="font-bold text-orange-700">
                          {editConfig.bonus_chefe_tipo === 'percentagem'
                            ? fmt((parseFloat(editConfig.comissao_comercial) || 0) * ((parseFloat(editConfig.bonus_chefe_valor) || 0) / 100))
                            : fmt(parseFloat(editConfig.bonus_chefe_valor) || 0)}
                        </span></div>
                        <div><span className="text-slate-400">Lucro</span><br /><span className="font-bold text-emerald-700">{(() => {
                          const r = parseFloat(editConfig.receita_empresa) || 0;
                          const cc = parseFloat(editConfig.comissao_comercial) || 0;
                          const cp = parseFloat(editConfig.comissao_parceiro) || 0;
                          const bc = editConfig.bonus_chefe_tipo === 'percentagem' ? cc * ((parseFloat(editConfig.bonus_chefe_valor) || 0) / 100) : (parseFloat(editConfig.bonus_chefe_valor) || 0);
                          return fmt(r - cc - cp - bc);
                        })()}</span></div>
                      </div>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditConfig(null)}>Cancelar</Button>
                  <Button onClick={handleSaveConfig} disabled={savingConfig || !editConfig?.company} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="save-config-btn">
                    {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Create Auto Dialog */}
        <Dialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (!v) { setCreateFilterType('todos'); setCreateFilterValue(''); } }}>
          <DialogContent className="sm:max-w-lg" data-testid="create-auto-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#2D3E6F]" /> Criar Novo Auto</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-slate-600">Mes</Label>
                  <Select value={String(createMonth)} onValueChange={(v) => setCreateMonth(parseInt(v))}>
                    <SelectTrigger className="mt-1" data-testid="create-auto-month"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTH_NAMES.slice(1).map((name, i) => <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Ano</Label>
                  <Input type="number" value={createYear} onChange={(e) => setCreateYear(parseInt(e.target.value))} className="mt-1" data-testid="create-auto-year" />
                </div>
              </div>
              <div>
                <Label className="text-sm text-slate-600">Filtrar por</Label>
                <Select value={createFilterType} onValueChange={(v) => { setCreateFilterType(v); setCreateFilterValue(''); }}>
                  <SelectTrigger className="mt-1" data-testid="create-auto-filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos (Global)</SelectItem>
                    <SelectItem value="comercial">Comercial</SelectItem>
                    <SelectItem value="parceiro">Parceiro</SelectItem>
                    <SelectItem value="equipa">Equipa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {createFilterType !== 'todos' && (
                <div>
                  <Label className="text-sm text-slate-600">
                    {createFilterType === 'comercial' ? 'Selecionar Comercial' :
                     createFilterType === 'parceiro' ? 'Selecionar Parceiro' :
                     'Selecionar Equipa/Chefe'}
                  </Label>
                  <Select value={createFilterValue} onValueChange={setCreateFilterValue}>
                    <SelectTrigger className="mt-1" data-testid="create-auto-filter-value"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                    <SelectContent>
                      {createFilterType === 'comercial' && filterOptions.users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.role === 'chefe_equipa' ? 'Chefe' : 'Comercial'})</SelectItem>
                      ))}
                      {createFilterType === 'parceiro' && filterOptions.partners.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                      {createFilterType === 'equipa' && filterOptions.teams.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={creating || (createFilterType !== 'todos' && !createFilterValue)} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2" data-testid="confirm-create-auto-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Criar Auto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Receipt Upload Dialog */}
        <Dialog open={!!receiptUpload} onOpenChange={(v) => { if (!v) setReceiptUpload(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="receipt-upload-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {receiptUpload?.type === 'receipt' ? (
                  <><Upload className="w-5 h-5 text-amber-500" /> Enviar Recibo</>
                ) : (
                  <><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Comprovativo de Pagamento</>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <p className="text-sm text-slate-600">
                {receiptUpload?.type === 'receipt'
                  ? `Envie o recibo verde ou fatura para ${receiptUpload?.userName || 'o utilizador'}.`
                  : `Envie o comprovativo de pagamento para ${receiptUpload?.userName || 'o utilizador'}. O auto sera marcado como liquidado.`
                }
              </p>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-[#4DD4E8] transition-colors cursor-pointer relative">
                <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Clique ou arraste o ficheiro (PDF, JPG, PNG)</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleReceiptUpload}
                  disabled={uploadingReceipt}
                  data-testid="receipt-file-input"
                />
                {uploadingReceipt && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl">
                    <Loader2 className="w-6 h-6 animate-spin text-[#2D3E6F]" />
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const KPICard = ({ icon, label, value, color, highlight }) => (
  <Card className={`rounded-2xl border-0 bg-gradient-to-br ${color} ${highlight ? 'ring-2 ring-emerald-400/50' : ''}`}>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-xs text-white/70">{label}</p>
        <p className="text-lg font-bold text-white mt-0.5">{value}</p>
      </div>
      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">{icon}</div>
    </CardContent>
  </Card>
);
